import { useCallback, useEffect, useRef, useState } from "react";
import { View, Text } from "react-native";
import { CompactActionButton } from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import { EMBER } from "../../../onboarding/theme";
import { useStytch, useStytchUser } from "../../../utils/useStytch";
import { getAuthCallbackUrl } from "../../../utils/getAuthCallbackUrl";
import { OTPInput } from "@src/packages/form/OTPInput/OTPInput";

export type EmailVerificationStatus = "verified" | "pending" | "unverified";
export type PhoneVerificationStatus = "verified" | "unverified";

/**
 * Derives the email verification status from the Stytch user object.
 * - "verified" if the email exists in the Stytch user's emails array and is verified
 * - "pending" if the email exists but is not yet verified (magic link sent but not clicked)
 * - "unverified" if no email or email not found on the Stytch user
 */
export function useEmailVerificationStatus(
  email: string | null,
): EmailVerificationStatus {
  const { user } = useStytchUser();

  if (!email) return "unverified";
  if (!user) return "unverified";

  const stytchEmail = user.emails.find(
    (e) => e.email.toLowerCase() === email.toLowerCase(),
  );

  if (!stytchEmail) return "unverified";
  if (stytchEmail.verified) return "verified";
  return "pending"; // exists in Stytch but not yet verified
}

/**
 * Email verification badge with actionable buttons.
 * - Verified: green pill badge (read-only)
 * - Unverified: solid "Verify" button that sends a magic link
 * - Pending: outline "Resend" button + "Check your email" hint
 */
export function EmailVerificationBadge({
  status,
  email,
  onError,
}: {
  status: EmailVerificationStatus;
  email?: string;
  /** Surface delivery failures beside the field rather than silently swallowing them. */
  onError?: (message: string | null) => void;
}) {
  const t = useTranslations();
  const stytch = useStytch();
  const [isSending, setIsSending] = useState(false);
  const [justSent, setJustSent] = useState(false);
  const justSentTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (justSentTimer.current) clearTimeout(justSentTimer.current);
    };
  }, []);

  const sendMagicLink = useCallback(async () => {
    if (!email || isSending) return;
    setIsSending(true);
    try {
      const callbackUrl = getAuthCallbackUrl();
      await stytch.magicLinks.email.send(email, {
        login_magic_link_url: callbackUrl,
        signup_magic_link_url: callbackUrl,
      });
      onError?.(null);
      setJustSent(true);
      justSentTimer.current = setTimeout(() => setJustSent(false), 5000);
    } catch {
      onError?.(t("you.email_badge.failed_to_send"));
    } finally {
      setIsSending(false);
    }
  }, [email, isSending, onError, stytch.magicLinks.email, t]);

  // Verified — hide completely (no badge needed)
  if (status === "verified") {
    return null;
  }

  // Unverified — solid "Verify" button
  if (status === "unverified") {
    return (
      <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
        <CompactActionButton
          label={t("you.email_badge.verify")}
          action="you.email_badge.verify"
          variant="primary"
          size="sm"
          loading={isSending}
          onPress={() => void sendMagicLink()}
        />
        {justSent && (
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 10,
              color: EMBER.colors.textFaint,
            }}
          >
            {t("you.email_badge.check_your_email")}
          </Text>
        )}
      </View>
    );
  }

  // Pending — outline "Resend" button + hint
  return (
    <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
      <CompactActionButton
        label={t("you.email_badge.resend")}
        action="you.email_badge.resend"
        variant="secondary"
        size="sm"
        loading={isSending}
        onPress={() => void sendMagicLink()}
      />
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 10,
          color: justSent ? EMBER.colors.green : EMBER.colors.textFaint,
        }}
      >
        {justSent
          ? t("you.email_badge.sent")
          : t("you.email_badge.check_your_email")}
      </Text>
    </View>
  );
}

/**
 * Derives phone verification status from the Stytch user object.
 */
export function usePhoneVerificationStatus(
  phone: string | null,
): PhoneVerificationStatus {
  const { user } = useStytchUser();

  if (!phone || !user) return "unverified";

  const normalized = phone.replace(/[^\d+]/g, "");
  const stytchPhone = user.phone_numbers.find(
    (p) => p.phone_number.replace(/[^\d+]/g, "") === normalized,
  );

  if (!stytchPhone) return "unverified";
  if (stytchPhone.verified) return "verified";
  return "unverified";
}

/**
 * Phone verification flow.
 *
 * Returns the inline control (the "Verify" button, or the code-input +
 * "Submit" once an OTP has been sent) plus any error message. The control
 * is meant to sit in the field's trailing `right` slot, while the error is
 * surfaced *below* the field row by the caller — keeping it out of the
 * bordered input box where it would otherwise be clipped.
 *
 * - Verified: `control` is null (nothing to show).
 * - Unverified: "Verify" button that sends SMS OTP, then shows code input.
 */
export function usePhoneVerification({
  status,
  phone,
}: {
  status: PhoneVerificationStatus;
  phone?: string;
}): {
  control: React.ReactNode;
  /** The six code boxes and Submit, shown under the phone field once sent. */
  codeEntry: React.ReactNode;
  error: string | null;
} {
  const t = useTranslations();
  const stytch = useStytch();
  const [isSending, setIsSending] = useState(false);
  const [methodId, setMethodId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // OTP method IDs belong to one phone number. Drop an in-progress flow if
  // the caller switches numbers so a code can never authenticate a stale
  // destination.
  useEffect(() => {
    setMethodId(null);
    setCode("");
    setError(null);
  }, [phone]);

  const sendOtp = useCallback(async () => {
    if (!phone || isSending) return;
    setIsSending(true);
    setError(null);
    try {
      const resp = await stytch.otps.sms.send(phone, {
        expiration_minutes: 10,
      });
      setMethodId(resp.method_id);
    } catch {
      setError(t("you.phone_badge.failed_to_send"));
    } finally {
      setIsSending(false);
    }
  }, [phone, isSending, stytch.otps.sms, t]);

  const verifyOtp = useCallback(
    async (entered: string) => {
      if (!methodId || entered.length !== 6 || isVerifying) return;
      setIsVerifying(true);
      setError(null);
      try {
        await stytch.otps.authenticate(entered, methodId, {
          session_duration_minutes: 60 * 24,
        });
        setMethodId(null);
        setCode("");
      } catch {
        setError(t("you.phone_badge.invalid_code"));
      } finally {
        setIsVerifying(false);
      }
    },
    [methodId, isVerifying, stytch.otps, t],
  );

  // Verified — nothing to render
  if (status === "verified") {
    return { control: null, codeEntry: null, error: null };
  }

  // OTP sent: the code is six boxes under the phone field, then Submit.
  if (methodId) {
    return {
      error,
      control: null,
      codeEntry: (
        <View style={{ marginTop: 10, gap: 10, alignItems: "flex-start" }}>
          <OTPInput
            length={6}
            appearance="light"
            autoFocus
            hasError={!!error}
            onChange={setCode}
            // The sixth digit sends the code, as Enter did in the one field.
            onComplete={(entered) => void verifyOtp(entered)}
          />
          <CompactActionButton
            label={t("you.phone_badge.submit")}
            action="you.phone_badge.submit"
            variant="primary"
            size="sm"
            loading={isVerifying}
            disabled={code.length !== 6}
            onPress={() => void verifyOtp(code)}
          />
        </View>
      ),
    };
  }

  // Unverified — show Verify button
  return {
    error,
    codeEntry: null,
    control: (
      <CompactActionButton
        label={t("you.phone_badge.verify")}
        action="you.phone_badge.verify"
        variant="primary"
        size="sm"
        loading={isSending}
        onPress={() => void sendOtp()}
      />
    ),
  };
}
